import { GameMode } from '@/contexts/GameModeContext';
import { modeAwareStorage } from '@/lib/managers/ModeAwareStorageManager';
import { formatDate, getEffectiveDate } from '@/lib/dateUtils';
/**
 * Centralized manager for game completion state persistence
 * Handles mode-specific completion checking and storage
 */
export class CompletionStateManager {
  
  /**
   * Check if a game is completed for a specific mode
   * Uses mode-aware CookieManager for proper state isolation
   */
  static hasCompletedGame(mode: GameMode): boolean {
    console.log(`Checking completion state for ${mode} mode`);
    try {
      // Read cookies explicitly for the target mode without switching global mode
      const gameStatus = modeAwareStorage.getCookieForMode('game_status', mode) as any;
      const completionState = modeAwareStorage.getCookieForMode('game_completion_state', mode);
      const savedDate = modeAwareStorage.getCookieForMode('game_persistence_date', mode);
      const today = formatDate(getEffectiveDate());

      // In DAILY mode, require a matching savedDate for completion to count
      if (mode === 'daily') {
        if (!savedDate) {
          console.log(`No persistence date found for ${mode} mode; treating as not completed for today.`);
          return false;
        }
        if (savedDate !== today) {
          console.log(`Ignoring stale completion for ${mode} mode due to date mismatch`, {
            savedDate,
            today,
            gameStatus,
            completionState,
            timestamp: new Date().toISOString()
          });
          return false;
        }
      }

      const completed = gameStatus === 'won' || gameStatus === 'lost' || completionState === 'true';

      console.log(`${mode} mode completion check:`, {
        gameStatus,
        completionState,
        savedDate,
        today,
        completed,
        timestamp: new Date().toISOString()
      });

      return completed;
    } catch (error) {
      console.error(`Error checking completion state for ${mode} mode:`, error);
      return false;
    }
  }
  
  /**
   * Mark a game as completed for the current mode
   * Uses mode-aware CookieManager for proper state isolation
   */
  static markGameCompleted(status: 'won' | 'lost'): void {
    const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
    
    console.log(`Marking game as ${status} for ${currentMode} mode`);
    
    try {
      // Use mode-aware storage
      modeAwareStorage.setGameStatus(status);
      modeAwareStorage.setCookie('game_completion_state', 'true');
      // Atomically stamp today's date to avoid missing persistence date
      const today = formatDate(getEffectiveDate());
      modeAwareStorage.setPersistenceDate(today);
      
      // DO NOT set lastPlayed here to prevent race conditions with streak calculation
      // The StreakManager will handle setting lastPlayed AFTER streak calculations are complete
      console.log('CompletionStateManager: Skipping lastPlayed update to prevent race conditions with streak calculation');
      
      console.log(`Successfully marked ${currentMode} mode game as ${status}`);
      
    } catch (error) {
      console.error(`Error marking game as completed for ${currentMode} mode:`, error);
      throw error;
    }
  }
  
  /**
   * Check if the completion screen should be shown
   * Mode-specific version that only checks the target mode
   */
  static shouldShowCompletion(mode?: GameMode): boolean {
    const targetMode = mode || (localStorage.getItem('babblegram_game_mode') as GameMode) || 'daily';
    
    try {
      // Only check if game is explicitly marked as completed for the specific mode
      const completed = this.hasCompletedGame(targetMode);
      console.log(`Should show completion for ${targetMode}: ${completed}`);
      return completed;
      
    } catch (error) {
      console.error(`Error checking shouldShowCompletion for ${targetMode}:`, error);
      return false;
    }
  }
  
  /**
   * Clear completion state for a specific mode
   */
  static clearCompletionState(mode: GameMode): void {
    console.log(`Clearing completion state for ${mode} mode`);
    try {
      // Remove cookies for the specified mode without switching global mode
      modeAwareStorage.removeCookieForMode('game_status', mode);
      modeAwareStorage.removeCookieForMode('game_completion_state', mode);
      console.log(`Successfully cleared completion state for ${mode} mode`);
    } catch (error) {
      console.error(`Error clearing completion state for ${mode} mode:`, error);
    }
  }
}